class ReusableModal extends HTMLElement {
  constructor () {
    super()
    this.open = false
    this.scrollTimeout = null
  }

  connectedCallback () {
    this.setupListeners()

    const modal = this.querySelector('.reusable-modal')
    const modalHeader = this.querySelector('.reusable-modal__header')
    const modalBody = this.querySelector('.reusable-modal__body')
    const modalClose = this.querySelector('.reusable-modal__close')
    const modalFooter = this.querySelector('.reusable-modal__footer')

    this.defaultStyles = {
      modalMaxWidth: modal.style.maxWidth || '',
      modalClasses: [...modal.classList],
      modalHeaderClasses: [...modalHeader.classList],
      modalBodyClasses: [...modalBody.classList],
      modalCloseClasses: [...modalClose.classList],
      modalFooterClasses: [...modalFooter.classList],
    }
  }

  setupListeners () {
    window.addEventListener('open:modal', this.handleOpen.bind(this))
    window.addEventListener('close:modal', this.handleClose.bind(this))
    this.closeButton.addEventListener('click', this.handleClose.bind(this))
    this.addEventListener('click', this.handleOutsideClick.bind(this))
    document.addEventListener('keydown', this.handleKeyDown.bind(this))
  }

  handleOrientationChange () {
    // Detect orientation change

    const video = this.querySelector('video') // Target your video element

    // Check if orientation is landscape
    if (screen.orientation.type.startsWith('landscape')) {
      // If in landscape mode, request fullscreen
      if (video.requestFullscreen) {
        video.requestFullscreen()
      } else if (video.webkitRequestFullscreen) { // For Safari/iOS
        video.webkitRequestFullscreen()
      } else if (video.msRequestFullscreen) { // For IE/Edge
        video.msRequestFullscreen()
      }
    } else {
      // Exit fullscreen on portrait mode - only if actually in fullscreen
      if (document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement) {
        if (document.exitFullscreen) {
          document.exitFullscreen()
        } else if (document.webkitExitFullscreen) { // For Safari/iOS
          document.webkitExitFullscreen()
        } else if (document.msExitFullscreen) { // For IE/Edge
          document.msExitFullscreen()
        }
      }
    }
  }

  handleOpen (event) {
    this.open = true
    const detail = event.detail || {}
    this.applyStyles(detail)
    window.dispatchEvent(new CustomEvent('show:page-obscure'))
    window.dispatchEvent(new CustomEvent('prevent:page-scroll'))

    const modalBody = this.querySelector('.reusable-modal__body')
    const focusableElements = this.getFocusableElements(modalBody)
    if (focusableElements.length) {
      focusableElements[0].focus()
    }

    // Add orientation change listener
    screen.orientation.addEventListener('change', this.handleOrientationChange.bind(this))

    // Fix for iOS Safari z-index issues
    this.fixIOSZIndexIssues()

    // Initialize scroll fader
    this.initScrollFader()

    // Set initial fader position
    this.updateFaderPosition()
  }

  fixIOSZIndexIssues () {
    const modal = this.querySelector('.reusable-modal')
    const modalHeader = this.querySelector('.reusable-modal__header')
    const modalClose = this.querySelector('.reusable-modal__close')

    // Force hardware acceleration and create a new stacking context
    if (modalHeader.classList.contains('absolute')) {
      modalHeader.style.transform = 'translateZ(0)'
      modalHeader.style.willChange = 'transform'
      modalHeader.style.zIndex = '999' // Use a very high z-index

      if (modalClose) {
        modalClose.style.transform = 'translateZ(0)'
        modalClose.style.willChange = 'transform'
        modalClose.style.zIndex = '1000' // Even higher z-index
      }

      // Ensure the modal container has a 3D context
      modal.style.transformStyle = 'preserve-3d'
    }
  }

  applyStyles (detail) {
    const modal = this.querySelector('.reusable-modal')
    const modalHeader = this.querySelector('.reusable-modal__header')
    const modalBody = this.querySelector('.reusable-modal__body')
    const modalFooter = this.querySelector('.reusable-modal__footer')
    const modalClose = this.querySelector('.reusable-modal__close')

    if (detail.width) {
      modal.style.maxWidth = detail.width
    }

    if (detail.padding === 'none') {
      modal.classList.remove('p-4', 'md:p-4')
    } else if (detail.padding) {
      modal.classList.add(...detail.padding)
    }

    if (detail.modalClassAdd) {
      modal.classList.add(...detail.modalClassAdd)
    }

    if (detail.modalClassRemove) {
      modal.classList.remove(...detail.modalClassRemove)
    }

    if (detail.headerClassAdd) {
      modalHeader.classList.add(...detail.headerClassAdd)
    }
    if (detail.headerClassRemove) {
      modalHeader.classList.remove(...detail.headerClassRemove)
    }

    if (detail.bodyClassAdd) {
      modalBody.classList.add(...detail.bodyClassAdd)
    }
    if (detail.bodyClassRemove) {
      modalBody.classList.remove(...detail.bodyClassRemove)
    }

    if (detail.closeClassAdd) {
      modalClose.classList.add(...detail.closeClassAdd)
    }
    if (detail.closeClassRemove) {
      modalClose.classList.remove(...detail.closeClassRemove)
    }

    // Add footer class handling
    if (detail.footerClassAdd) {
      modalFooter.classList.add(...detail.footerClassAdd)
    }
    if (detail.footerClassRemove) {
      modalFooter.classList.remove(...detail.footerClassRemove)
    }

    // Update fader position after style changes
    setTimeout(() => {
      this.updateFaderPosition()
    }, 10)
  }

  handleClose () {
    this.open = false
    window.dispatchEvent(new CustomEvent('allow:page-scroll'))
    setTimeout(() => {
      this.resetStyles()
      this.clearModal()
    }, 250)
    window.dispatchEvent(new CustomEvent('hide:page-obscure'))

    // Remove orientation change listener
    screen.orientation.removeEventListener('change', this.handleOrientationChange.bind(this))

    // Clean up scroll fader
    this.cleanupScrollFader()
  }

  resetStyles () {
    const modal = this.querySelector('.reusable-modal')
    const modalHeader = this.querySelector('.reusable-modal__header')
    const modalClose = this.querySelector('.reusable-modal__close')
    const modalFooter = this.querySelector('.reusable-modal__footer')
    modal.style.maxWidth = this.defaultStyles.modalMaxWidth
    modal.className = ''
    modal.classList.add(...this.defaultStyles.modalClasses)

    // Reset iOS fixes
    modal.style.transformStyle = ''

    modalHeader.className = ''
    modalHeader.classList.add(...this.defaultStyles.modalHeaderClasses)
    modalHeader.style.transform = ''
    modalHeader.style.willChange = ''
    modalHeader.style.zIndex = ''

    modalClose.className = ''
    modalClose.classList.add(...this.defaultStyles.modalCloseClasses)
    modalClose.style.transform = ''
    modalClose.style.willChange = ''
    modalClose.style.zIndex = ''

    modalFooter.className = ''
    modalFooter.classList.add(...this.defaultStyles.modalFooterClasses)

    // Reset fader position
    const fader = this.querySelector('.reusable-modal__body-fader')
    if (fader) {
      fader.style.bottom = ''
    }
  }

  handleOutsideClick (event) {
    if (!this.open) return
    const modalContainer = this.querySelector('.reusable-modal')
    if (this.open && !modalContainer.contains(event.target)) {
      this.handleClose()
    }
  }

  handleKeyDown (event) {
    if (!this.open) return

    const modal = this.querySelector('.reusable-modal')
    const focusableElements = this.getFocusableElements(modal)
    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    if (event.key === 'Tab') {
      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault()
          lastElement.focus()
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault()
          firstElement.focus()
        }
      }
    } else if (event.key === 'Escape') {
      this.handleClose()
    }
  }

  clearModal () {
    this.querySelector('.reusable-modal__title').innerHTML = ''
    this.querySelector('.reusable-modal__body').innerHTML = ''
    this.querySelector('.reusable-modal__footer').innerHTML = ''
    this.querySelector('.reusable-modal__body-wrapper').scrollTo({ top: 0, behavior: 'instant' })
  }

  initScrollFader () {
    const modalWrapper = this.querySelector('.reusable-modal__body-wrapper')
    const modalBody = this.querySelector('.reusable-modal__body')
    const fader = this.querySelector('.reusable-modal__body-fader')

    if (!modalWrapper || !modalBody || !fader) return

    this.updateFaderVisibility()

    // Add scroll listener with throttling - listen on wrapper (scroll container)
    this.scrollHandler = this.throttle(() => {
      this.updateFaderVisibility()
    }, 16) // ~60fps

    modalWrapper.addEventListener('scroll', this.scrollHandler)

    // Also check when content changes (e.g., dynamic content loaded)
    this.resizeObserver = new ResizeObserver(() => {
      this.updateFaderVisibility()
      this.updateFaderPosition()
    })
    this.resizeObserver.observe(modalBody)

    // Watch for content changes via innerHTML
    this.mutationObserver = new MutationObserver(() => {
      this.updateFaderVisibility()
      this.updateFaderPosition()
    })
    this.mutationObserver.observe(modalBody, {
      childList: true,
      subtree: true,
    })
  }

  updateFaderVisibility () {
    const modalWrapper = this.querySelector('.reusable-modal__body-wrapper')
    const fader = this.querySelector('.reusable-modal__body-fader')

    if (!modalWrapper || !fader) return

    const { scrollTop, scrollHeight, clientHeight } = modalWrapper
    const isScrollable = scrollHeight > clientHeight
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight
    const threshold = 20 // Show fader when more than 20px from bottom

    if (isScrollable && distanceFromBottom > threshold) {
      fader.classList.add('show')
    } else {
      fader.classList.remove('show')
    }
  }

  updateFaderPosition () {
    const fader = this.querySelector('.reusable-modal__body-fader')
    const footer = this.querySelector('.reusable-modal__footer')
    const modal = this.querySelector('.reusable-modal')

    if (!fader || !footer || !modal) return

    const isFooterVisible = !footer.classList.contains('hidden')

    // Dynamically detect modal padding
    const hasPadding = modal.classList.contains('p-4') || modal.classList.contains('md:p-4')
    const padding = hasPadding ? 16 : 0 // 1rem = 16px, or 0 if no padding

    if (isFooterVisible) {
      // Position above footer - account for footer height + margin
      const footerHeight = footer.offsetHeight
      const footerMarginTop = 16 // mt-4 = 1rem = 16px
      const bottomOffset = footerHeight + footerMarginTop + padding

      fader.style.bottom = `${bottomOffset}px`
    } else {
      // Position at bottom with or without padding
      fader.style.bottom = `${padding}px`
    }
  }

  cleanupScrollFader () {
    const modalWrapper = this.querySelector('.reusable-modal__body-wrapper')

    if (modalWrapper && this.scrollHandler) {
      modalWrapper.removeEventListener('scroll', this.scrollHandler)
      this.scrollHandler = null
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }

    if (this.mutationObserver) {
      this.mutationObserver.disconnect()
      this.mutationObserver = null
    }

    const fader = this.querySelector('.reusable-modal__body-fader')
    if (fader) {
      fader.classList.remove('show')
    }
  }

  throttle (func, limit) {
    let inThrottle
    return function () {
      const args = arguments
      const context = this
      if (!inThrottle) {
        func.apply(context, args)
        inThrottle = true
        setTimeout(() => {
          inThrottle = false
        }, limit)
      }
    }
  }

  getFocusableElements (container) {
    return container.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
  }

  set open (value) {
    this.setAttribute('open', value)
  }

  get open () {
    return this.hasAttribute('open')
  }

  get closeButton () {
    return this.querySelector('.reusable-modal__close')
  }
}

if (!customElements.get('reusable-modal')) {
  customElements.define('reusable-modal', ReusableModal)
}
